"use client";

/**
 * Intake summary read-back showing what was collected during intake.
 *
 * Extracted from `compass-report.jsx:22-43` (`.rp-*` classes).
 */

import { type ProductIdentity, ProductVendorLabel } from "./product-identity";

/**
 * A product the intake captured, as `IntakeReportSummaryProduct` projects it.
 *
 * The id is the graph candidate id, and is what lets the report match the
 * selection to the persisted product-selection artifact's
 * `structure.selectedProductId` by identity. Optional because threads that
 * predate the projection carrying it report a name alone.
 */
export interface ReportSummaryProduct extends ProductIdentity {
  id?: string;
}

export interface ReportSummary {
  path: "A" | "B";
  /** Defaults to true; agentic intake has no buyer-selected approach. */
  showApproach?: boolean;
  vendor?: string;
  problem?: string;
  problems?: string[];
  context?: Record<string, string>;
  workflows?: string[];
  metrics?: string[];
  products?: ReportSummaryProduct[];
}

export interface ReportViewProps {
  summary: ReportSummary;
}

function Card({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-(--bg-white) border border-(--border-subtle) rounded-(--radius-container) px-4 py-[14px] shadow-(--shadow-xs)">
      <div className="text-[11px] tracking-[.06em] uppercase text-(--fg-muted) font-normal mb-[7px]">
        {label}
      </div>
      {children}
    </div>
  );
}

export function ReportView({ summary: s }: ReportViewProps) {
  return (
    <div className="max-w-[760px] mx-auto px-7 py-[26px] pb-[50px]">
      <div className="text-[11px] tracking-[.09em] uppercase text-(--fg-muted) font-normal mb-[14px]">
        What you told us during intake
      </div>
      <div className="flex flex-col gap-[10px]">
        {s.showApproach !== false && (
          <Card label="Approach">
            <div className="text-[14px] font-normal text-(--fg-primary)">
              {s.path === "A" ? "Vendor evaluation" : "General problem"}
            </div>
          </Card>
        )}

        {s.path === "A" && s.vendor && (
          <Card label="Vendor">
            <div className="text-[14px] font-normal text-(--fg-primary)">
              {s.vendor}
            </div>
          </Card>
        )}

        {s.path === "A"
          ? s.problems &&
            s.problems.length > 0 && (
              <Card label="Problems to model">
                <ul className="m-0 pl-[18px] flex flex-col gap-1">
                  {s.problems.map((p, i) => (
                    <li key={i} className="text-[13.5px] text-(--fg-primary)">
                      {p}
                    </li>
                  ))}
                </ul>
              </Card>
            )
          : s.problem && (
              <Card label="Problem">
                <div className="text-[14px] font-normal text-(--fg-primary)">
                  {s.problem}
                </div>
              </Card>
            )}

        {/* Both directions collect deployment context and workflows, so these
            render off the data being present rather than off the path. */}
        {s.context && (
          <Card label="Context">
            <div className="flex flex-wrap gap-[7px]">
              {Object.values(s.context).map((v, i) => (
                <span
                  key={i}
                  className="text-[12.5px] font-normal text-(--fg-secondary) bg-(--bg-canvas) border border-(--border-subtle) rounded-full px-[11px] py-[5px]"
                >
                  {v}
                </span>
              ))}
            </div>
          </Card>
        )}

        {s.workflows && s.workflows.length > 0 && (
          <Card label="Workflows">
            <ul className="m-0 flex list-none flex-wrap gap-[7px] p-0">
              {s.workflows.map((w, i) => (
                <li
                  key={i}
                  className="text-[12.5px] font-normal text-(--fg-secondary) bg-(--bg-canvas) border border-(--border-subtle) rounded-full px-[11px] py-[5px]"
                >
                  {w}
                </li>
              ))}
            </ul>
          </Card>
        )}

        {s.metrics && s.metrics.length > 0 && (
          <Card label="Success metrics">
            {/* A genuine list, so it stays a list for assistive tech — the pill
                styling is purely visual. */}
            <ul className="m-0 flex list-none flex-wrap gap-[7px] p-0">
              {s.metrics.map((m, i) => (
                <li
                  key={i}
                  className="text-[12.5px] font-normal text-(--fg-secondary) bg-(--bg-canvas) border border-(--border-subtle) rounded-full px-[11px] py-[5px]"
                >
                  {m}
                </li>
              ))}
            </ul>
          </Card>
        )}

        {s.products && s.products.length > 0 && (
          <Card label="Products modeled">
            <div className="flex flex-wrap gap-[7px]">
              {s.products.map((p, i) => (
                <span
                  key={i}
                  className="bg-(--bg-canvas) border border-(--border-subtle) rounded-full px-[11px] py-[5px]"
                >
                  <ProductVendorLabel
                    identity={p}
                    nameClassName="font-normal"
                  />
                </span>
              ))}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
